import { useCallback } from 'react';

export const ALL_USERS_ROLE = 'all-users';

/**
 * Hook to check permissions for Sanity components
 * Compares the component's userRoles array with the logged-in user's customer group key
 */
const useSanityPermissionsChecker = () => {
  /**
   * Gets the customer group key directly (no formatting needed)
   * @param customerGroup - The user's customer group object
   * @returns The customer group key
   */
  const getCustomerGroupKey = useCallback((customerGroup: any) => {
    return customerGroup?.obj?.key ?? '';
  }, []);

  /**
   * Checks if any value in the userRoles array matches the customer group key
   * @param userRoles - Array of roles from the Sanity component
   * @param customerGroupKey - The customer group key
   * @returns true if there's a match, false otherwise
   */
  const hasMatchingRole = useCallback((userRoles: string[] | undefined, customerGroupKey: string) => {
    // If userRoles is not defined, allow access (similar to Contentful without tags)
    if (!userRoles || !Array.isArray(userRoles) || userRoles.length === 0) {
      return true;
    }

    // Check if it contains the "all-users" wildcard (allows access to any user)
    const hasAllUsersRole = userRoles.some((role) => {
      const normalizedRole = typeof role === 'string' ? role.toLowerCase().trim() : String(role).toLowerCase().trim();
      return normalizedRole === ALL_USERS_ROLE || normalizedRole === 'allusers';
    });

    if (hasAllUsersRole) {
      return true;
    }

    // If there's no customer group and no wildcard, deny access
    if (!customerGroupKey) {
      return false;
    }

    // Check if any value in the userRoles array matches the customer group key (direct comparison)
    return userRoles.some((role) => {
      const roleValue = typeof role === 'string' ? role : String(role);
      return roleValue === customerGroupKey;
    });
  }, []);

  /**
   * Recursively checks permissions in all nested levels of a Sanity object
   * @param obj - The object to check (can be nested)
   * @param customerGroupKey - The customer group key
   * @param visited - Set to track visited objects and prevent circular references
   * @returns true if all userRoles in the object tree have permissions, false otherwise
   */
  const checkNestedPermissions = useCallback(
    (obj: any, customerGroupKey: string, visited: Set<any> = new Set()): boolean => {
      // Prevent circular references
      if (obj === null || obj === undefined || typeof obj !== 'object') {
        return true;
      }

      // Skip if already visited (circular reference protection)
      if (visited.has(obj)) {
        return true;
      }

      visited.add(obj);

      // Check if current object has userRoles
      if (obj.userRoles && Array.isArray(obj.userRoles)) {
        const hasPermission = hasMatchingRole(obj.userRoles, customerGroupKey);
        if (!hasPermission) {
          return false;
        }
      }

      // Recursively check all nested objects and arrays
      for (const key in obj) {
        if (key === 'userRoles') {
          continue; // Already checked above
        }

        const value = obj[key];

        if (Array.isArray(value)) {
          // Check each item in the array
          for (const item of value) {
            if (typeof item === 'object' && item !== null) {
              const hasPermission = checkNestedPermissions(item, customerGroupKey, visited);
              if (!hasPermission) {
                return false;
              }
            }
          }
        } else if (typeof value === 'object' && value !== null) {
          // Recursively check nested objects
          const hasPermission = checkNestedPermissions(value, customerGroupKey, visited);
          if (!hasPermission) {
            return false;
          }
        }
      }

      return true;
    },
    [hasMatchingRole],
  );

  /**
   * Checks permissions for a Sanity object with all its nested levels
   * @param item - The Sanity object (can have nested components)
   * @param customerGroup - The user's customer group object
   * @returns true if all userRoles in the object tree have permissions, false otherwise
   */
  const checkNestedItemPermission = useCallback(
    (item: any, customerGroup: any) => {
      const customerGroupKey = getCustomerGroupKey(customerGroup);
      return checkNestedPermissions(item, customerGroupKey);
    },
    [getCustomerGroupKey, checkNestedPermissions],
  );

  /**
   * Checks only the item's own userRoles (no recursion into nested items).
   * Use this when you want to show a section if the user has access to the section,
   * and then filter nested items (e.g. links) separately by their own permissions.
   */
  const checkItemPermission = useCallback(
    (item: any, customerGroup: any) => {
      const customerGroupKey = getCustomerGroupKey(customerGroup);
      if (!item || typeof item !== 'object') return true;
      if (!item.userRoles || !Array.isArray(item.userRoles)) return true;
      return hasMatchingRole(item.userRoles, customerGroupKey);
    },
    [getCustomerGroupKey, hasMatchingRole],
  );

  return {
    checkNestedItemPermission,
    checkItemPermission,
  };
};

export default useSanityPermissionsChecker;
